import { useState, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import useDebounce from 'react-use/esm/useDebounce';

interface RepoEntry {
  owner: string;
  name: string;
}

export function useGitHubRepos(
  host: string | undefined,
  accessToken: string | undefined,
) {
  const scaffolderApi = useApi(scaffolderApiRef);
  const [repos, setRepos] = useState<RepoEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRepos = useCallback(() => {
    if (!scaffolderApi.autocomplete || !accessToken || !host) {
      setRepos([]);
      return;
    }

    setLoading(true);
    scaffolderApi
      .autocomplete({
        token: accessToken,
        resource: 'repositoriesWithOwner',
        provider: 'github',
        context: { host },
      })
      .then(({ results }) => {
        setRepos(
          results.map(r => {
            const [rOwner, rName] = r.id.split('/');
            return { owner: rOwner, name: rName };
          }),
        );
      })
      .catch(() => {
        setRepos([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [scaffolderApi, accessToken, host]);

  useDebounce(fetchRepos, 500, [fetchRepos]);

  const getReposForOwner = useCallback(
    (owner: string) => repos.filter(r => r.owner === owner),
    [repos],
  );

  return { repos, loading, getReposForOwner };
}
